import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react'
import {
  AppShell,
  ErrorBoundary,
  PageBody,
  PermissionProvider,
  Spinner,
  StatusBanner,
} from './components/index.js'
import { Overview } from './screens/Overview/Overview.js'
import { SignIn } from './screens/SignIn/SignIn.js'

/**
 * Every screen but the first two, fetched when it is opened.
 *
 * The desk was one file. Somebody typing their password waited for the class
 * room, both editors, the Markdown editor, the JSON editor, the icon picker
 * and the charts to arrive before the password box could be drawn — 932 kB of
 * JavaScript, 280 kB over the wire, to show two fields and a button.
 *
 * `Overview` stays eager because it is what everybody sees the moment they
 * are in, and `SignIn` because it is what everybody sees before that. A
 * screen behind a rail item, a record behind a row, or a sheet behind a menu
 * is worth one round trip at the moment it is asked for.
 *
 * Named exports, so each one is unwrapped into the default `lazy` wants —
 * the alternative is a default export per screen, which is a worse rule for
 * the sake of a nicer line here.
 */
function at<T, K extends keyof T>(load: () => Promise<T>, name: K): T[K] {
  /* `lazy` returns its own component type, and the screen's props are what
     every call site is checked against — so the cast is to the screen, not
     away from it. Getting `name` wrong is still a compile error. */
  return lazy(() =>
    load().then((m) => ({ default: m[name] as unknown as React.ComponentType })),
  ) as unknown as T[K]
}

const Members = at(() => import('./screens/Members/Members.js'), 'Members')
const MemberRecord = at(() => import('./screens/Members/MemberRecord.js'), 'MemberRecord')
const Providers = at(() => import('./screens/Providers/Providers.js'), 'Providers')
const ProviderRecord = at(
  () => import('./screens/ProviderRecord/ProviderRecord.js'),
  'ProviderRecord',
)
const Payments = at(() => import('./screens/Payments/Payments.js'), 'Payments')
const Bookings = at(() => import('./screens/Bookings/Bookings.js'), 'Bookings')
const BookingRecord = at(() => import('./screens/Bookings/BookingRecord.js'), 'BookingRecord')
const Classes = at(() => import('./screens/Classes/Classes.js'), 'Classes')
const Events = at(() => import('./screens/Events/Events.js'), 'Events')
const ClassEditor = at(() => import('./screens/ClassEditor/ClassEditor.js'), 'ClassEditor')
const ClassRecord = at(() => import('./screens/ClassRecord/ClassRecord.js'), 'ClassRecord')
const ClassRoom = at(() => import('./screens/ClassRoom/ClassRoom.js'), 'ClassRoom')
const EventEditor = at(() => import('./screens/EventEditor/EventEditor.js'), 'EventEditor')
const NoAccess = at(() => import('./screens/NoAccess/NoAccess.js'), 'NoAccess')
const NotFound = at(() => import('./screens/NotFound/NotFound.js'), 'NotFound')
const Settings = at(() => import('./screens/Settings/Settings.js'), 'Settings')
const Activity = at(() => import('./screens/Activity/Activity.js'), 'Activity')
const SupportInbox = at(() => import('./screens/SupportInbox/SupportInbox.js'), 'SupportInbox')
const SupportMessageRecord = at(
  () => import('./screens/SupportInbox/SupportMessageRecord.js'),
  'SupportMessageRecord',
)
const ProfileSheet = at(() => import('./screens/Profile/ProfileSheet.js'), 'ProfileSheet')
const PasswordSheet = at(() => import('./screens/Profile/PasswordSheet.js'), 'PasswordSheet')

import {
  useGetPermissionsQuery,
  useRestoreMutation,
  useSignOutMutation,
} from './screens/SignIn/api/signIn.api.js'
import { onSessionLapsed, SESSION_ENDED, setAccessToken } from './store/axiosBaseQuery.js'
import { rootApi } from './store/rootApi.js'
import { useAppDispatch, useAppSelector } from './store/index.js'
import { restoreFinished, selectRestoring, signedIn, signedOut } from './store/session.slice.js'
import { useGetDashboardQuery } from './screens/Overview/api/overview.api.js'
import { ATTENTION_DESTINATION } from './screens/Overview/core/constants.js'
import { ACTING_PERMISSIONS, RAIL, READ_ONLY_SAID } from './core/constants.js'
import { readPlace, refusalFor, samePlace, writePlace, type Place } from './lib/whereYouAre.js'

/**
 * The desk's frame, and who is behind it.
 *
 * The screens are switched by a key shaped exactly like a route key, and that
 * key now lives in the address bar — `lib/whereYouAre.ts` is the only thing
 * that knows what an address looks like, so moving the desk to real paths one
 * day changes that file and nothing else.
 *
 * **The session used to be a literal.** The rail read *"Signed out · not signed
 * in yet"*, `PermissionProvider` was handed a hard-coded array, and no request
 * ever carried a token — the whole authentication layer existed and nothing
 * called it. It is wired here because this is the one place that can: the rail,
 * the permission context and the error boundary are the frame, not a screen.
 */
/**
 * Whether this page load has already presented its refresh cookie.
 *
 * Module scope, so it survives the mount/unmount/remount that `StrictMode`
 * performs, and resets on a real reload — which is exactly when the cookie
 * should be tried again.
 */
let restoreAttempted = false

export function App() {
  const dispatch = useAppDispatch()
  const restoring = useAppSelector(selectRestoring)
  const user = useAppSelector((s) => s.session.user)

  /**
   * A reset link opens the desk at `/reset-password?token=…`, read once at mount
   * rather than fresh from `window.location.search` on every render.
   *
   * It used to be read inline in the render body below, every time `App` rendered —
   * which was fine until the address bar itself needed cleaning up (see the effect
   * further down): stripping `?token=…` from the URL would have made that same inline
   * read go back to `null` on the very next render, and `chooseNewPassword` would then
   * have sent an empty token to a reset already in progress. Read once, into state, so
   * the value this component actually uses cannot depend on what the address bar
   * currently shows.
   */
  const [resetToken] = useState(() => new URLSearchParams(window.location.search).get('token'))

  const [restore] = useRestoreMutation()
  const [signOut] = useSignOutMutation()
  /**
   * Why the session ended, when one did.
   *
   * 2.16 is the phone's version of this and the desk needs its own: being
   * idled out, being removed from the team and being suspended are three
   * different sentences, and the screen said none of them.
   */
  const [ended, setEnded] = useState<string | null>(null)

  /** The pending silent renewal, if one is scheduled. See `scheduleRenewal`. */
  const renewalTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  /**
   * A 401 on a request that carried a token means the session went.
   *
   * The whole cache goes with it. Leaving it would show the next person to sign
   * in on this machine the last person's tables until each one refetched.
   *
   * Moved above the effects that use it (it used to sit near the bottom of the
   * component) so the silent-renewal timer below, which also calls it, is not
   * reaching forward at a name the reader has not seen defined yet. It also
   * cancels any renewal still pending — signing out is itself a reason the
   * session ended, and a timer left running would otherwise fire a refresh
   * request a few minutes after nobody is here to use its answer.
   */
  const lapse = useCallback(
    (why?: string) => {
      if (renewalTimer.current !== null) {
        clearTimeout(renewalTimer.current)
        renewalTimer.current = null
      }
      setAccessToken(null)
      dispatch(signedOut())
      dispatch(rootApi.util.resetApiState())
      if (why) setEnded(why)
    },
    [dispatch],
  )

  /**
   * Keeping a 15-minute access token alive under somebody who is still here.
   *
   * `ACCESS_TOKEN_TTL` on the server is 900 seconds, and until this there was
   * nothing in the desk that ever renewed it early — every request just used
   * whatever token it had, and the first one to go out after it expired was
   * read as the session itself having ended. Staying on one screen for fifteen
   * minutes, which a provider record or a long call with a member both do
   * easily, signed somebody out from under their own cursor.
   *
   * Scheduled a little before the token's own expiry rather than waited out:
   * `access_expires_in` is how long the **current** token is good for, read off
   * both the sign-in response and this call's own response, so each renewal
   * reschedules the next one from the fresh number the server just handed back
   * rather than assuming it never changes.
   *
   * Only a **failed** refresh signs anyone out here — that is the one honest
   * signal that the longer-lived refresh cookie itself is gone, as opposed to
   * the short-lived access token merely having run down on schedule.
   */
  const scheduleRenewal = useCallback(
    (expiresInSeconds: number) => {
      if (renewalTimer.current !== null) clearTimeout(renewalTimer.current)
      /* A minute of margin, or half the lifetime for anything shorter than
         that — so a short TTL (tests, or a future config change) can never
         produce a negative delay that fires the renewal immediately in a loop. */
      const marginSeconds = Math.min(60, expiresInSeconds / 2)
      const delayMs = Math.max(0, (expiresInSeconds - marginSeconds) * 1000)
      renewalTimer.current = setTimeout(() => {
        void (async () => {
          try {
            const session = await restore().unwrap()
            setAccessToken(session.access_token)
            scheduleRenewal(session.access_expires_in)
          } catch {
            // The refresh cookie itself is gone or invalid — a real end to
            // the session, not a hiccup worth retrying silently.
            lapse(SESSION_ENDED.SESSION_IDLE_EXPIRED)
          }
        })()
      }, delayMs)
    },
    [restore, lapse],
  )

  /**
   * What this person may do, read once a session exists.
   *
   * Skipped while signed out, because it is an admin route and asking it
   * without a token is a 401 that would trip the lapsed-session handler on
   * every load of the sign-in screen.
   */
  const permissions = useGetPermissionsQuery(undefined, { skip: user === null })

  /**
   * The refresh cookie, tried **once** before anything is drawn.
   *
   * Once means once per page load, not once per effect run. `StrictMode`
   * invokes an effect twice in development, and the refresh token **rotates**:
   * the first call succeeds and revokes the token the second call is still
   * holding, so the second gets a 401 — and because it resolves last, its
   * failure is the answer the app keeps. The desk signed in perfectly and
   * signed you out on every F5, with a 200 and a 401 side by side in the log
   * saying so.
   *
   * A module-level flag rather than a ref: it has to survive the component
   * being torn down and remounted, which is the whole of what StrictMode
   * simulates. It is also the honest description of the thing — "this page has
   * already tried its cookie" is a fact about the page, not about a component.
   *
   * This is not only a development problem. Two tabs opened together, or any
   * retry of a request that rotates a credential, lose the same way; the guard
   * is what makes one page load make one attempt.
   */
  useEffect(() => {
    if (restoreAttempted) return
    restoreAttempted = true
    /*
     * The two halves are separate on purpose.
     *
     * This was `.then(record the session).catch(() => restoreFinished())`,
     * and that catch was reachable from inside the `then`: a change to the
     * session's shape would have thrown while recording a **real** session,
     * been caught, and drawn the sign-in screen at somebody who was signed
     * in — with nothing anywhere saying why.
     *
     * The same shape told the first administrator on the live desk that their
     * reset link had expired after it had worked. Only the call's own failure
     * means "there is no session"; anything after it is a bug and is allowed
     * to be loud.
     */
    void (async () => {
      let session
      try {
        session = await restore().unwrap()
      } catch {
        // The ordinary case on a cold open: no cookie, nobody to restore.
        dispatch(restoreFinished())
        return
      }
      setAccessToken(session.access_token)
      scheduleRenewal(session.access_expires_in)
      dispatch(
        signedIn({
          fullName: session.account.full_name,
          roleName: session.account.admin_role ?? 'no role',
          permissions: [],
          rolesWith: {},
        }),
      )
    })()
    /**
     * **No cleanup, and no `cancelled` flag.**
     *
     * There was one, and with the guard above it became the bug: StrictMode
     * runs the effect, tears it down, and runs it again — so the cleanup from
     * the first run flipped `cancelled` before the one real request came back,
     * and the answer was dropped on the floor. The refresh returned 200, the
     * log said so, and the desk showed the sign-in screen.
     *
     * Cancelling was guarding against setting state after unmount, which React
     * has not warned about since 18 and which is harmless here anyway: the
     * store outlives the component, and this dispatch is the whole reason the
     * page is waiting. The thing actually worth guarding — asking twice — is
     * what `restoreAttempted` does.
     */
  }, [dispatch, restore, scheduleRenewal])

  /** Once the permissions land, the session carries the real answer. */
  useEffect(() => {
    if (!permissions.data) return
    dispatch(
      signedIn({
        fullName: permissions.data.full_name,
        roleName: permissions.data.role,
        permissions: permissions.data.can,
        rolesWith: permissions.data.roles_with,
      }),
    )
  }, [dispatch, permissions.data])

  useEffect(() => onSessionLapsed((why) => lapse(why)), [lapse])

  /**
   * The `?token=…` a reset link opens with, scrubbed from the address bar once React
   * has read it — not spent yet, just no longer sitting in a URL that a reload, a
   * bookmark, or the browser's own Back button could hand back to `SignIn` as if it
   * were a fresh, live-looking link. (The token still works until it is actually used
   * or found expired — this only stops the *address bar* from offering it again.)
   */
  useEffect(() => {
    if (resetToken) window.history.replaceState({}, '', window.location.pathname)
  }, [resetToken])

  if (restoring) {
    return (
      <div className="grid min-h-full place-items-center">
        <Spinner label="Checking whether you are signed in" />
      </div>
    )
  }

  if (!user) {
    return (
      <SignIn
        /**
         * Somebody is in. Record it, which is what makes the desk render.
         *
         * This was `permissions.refetch()` and nothing else — and the
         * permissions query is **skipped while nobody is signed in**, so
         * refetching it did nothing at all. A correct password and a correct
         * code left the sign-in screen sitting there: the session existed,
         * the token was set, the server had issued it, and the desk never
         * drew. Nothing in the test suite could see it, because every test
         * starts from a store that already has somebody in it.
         *
         * The permissions arrive a moment later and replace the empty list,
         * exactly as they do on the restore path above.
         */
        onSignedIn={(session) => {
          setEnded(null)
          scheduleRenewal(session.access_expires_in)
          dispatch(
            signedIn({
              fullName: session.account.full_name,
              roleName: session.account.admin_role ?? 'no role',
              permissions: [],
              rolesWith: {},
            }),
          )
        }}
        {...(resetToken ? { resetToken } : {})}
        {...(ended ? { signedOutBecause: ended } : {})}
      />
    )
  }

  /**
   * Nothing of the desk until this person's permissions are known.
   *
   * The session lands a moment before them, with an empty list — and the desk
   * drew with that list: the read-only line flashed for every role, and a
   * page this role may not open fired its requests (two 403s on Payments for
   * Support) before the refusal could be decided. One spinner is the honest
   * picture of that moment.
   */
  /* And until they are in the session, which is an effect later: for one
     render the answer has arrived and the session still says nothing. */
  const settling =
    !permissions.isError &&
    (!permissions.data || (permissions.data.can.length > 0 && user.permissions.length === 0))
  if (settling) {
    return (
      <div className="grid min-h-full place-items-center">
        <Spinner label="Opening the desk" />
      </div>
    )
  }

  return (
    <Desk
      user={user}
      onSignOut={() => {
        /**
         * The request has to go out **before** the token that authenticates
         * it is cleared, not after.
         *
         * `lapse()` nulls the in-memory access token synchronously; the token
         * is only attached to a request at the moment axios dispatches it
         * (`axiosBaseQuery.ts`'s request interceptor). Firing both at once —
         * this used to be `void signOut(); lapse()` — let `lapse()` win the
         * race every time: the sign-out call left with no `Authorization`
         * header, the server's `/sign-out` route requires one and never ran,
         * and the httpOnly refresh cookie it revokes was never touched. The
         * screen looked signed out; reloading it signed you right back in.
         *
         * Awaited rather than left truly fire-and-forget, so the token is
         * still there when the request is dispatched — but the UI does not
         * wait on it: `lapse()` still runs the instant the call settles, and
         * a failed sign-out call (the network, or a session already gone)
         * clears local state exactly the same as a successful one, because
         * either way there is nothing left here to be signed in to.
         */
        void (async () => {
          await signOut().unwrap().catch(() => {})
          lapse()
        })()
      }}
    />
  )
}

/**
 * The desk itself, once somebody is behind it.
 *
 * Split from `App` so the permission provider wraps everything that reads from
 * it and nothing above it has to care.
 */
function Desk({
  user,
  onSignOut,
}: {
  user: {
    fullName: string
    roleName: string
    permissions: string[]
    rolesWith: Record<string, string[]>
  }
  onSignOut: () => void
}) {
  /* Where you are comes from the address bar, so a reload lands where you
     were rather than on Overview. See `lib/whereYouAre.ts`. */
  const landed = readPlace(window.location.pathname)
  const [section, setSection] = useState(landed.section)
  /** Which record is open, if one is. Kept in step with the address below. */
  const [openProvider, setOpenProvider] = useState<string | null>(
    landed.section === 'providers' ? (landed.id ?? null) : null,
  )
  /** Which member is open, if one is. */
  const [openMember, setOpenMember] = useState<string | null>(
    landed.section === 'members' ? (landed.id ?? null) : null,
  )
  /** Whether the record being opened was opened in order to suspend somebody. */
  const [suspendOnOpen, setSuspendOnOpen] = useState(false)
  /** Whether the provider record being opened was opened in order to pause. */
  const [pauseOnOpen, setPauseOnOpen] = useState(false)
  /** 9.5a's "find the payment" opens 9.3 filtered to a reference. */
  const [paymentRef, setPaymentRef] = useState<string | undefined>(undefined)
  const [openBooking, setOpenBooking] = useState<string | null>(
    landed.section === 'bookings' ? (landed.id ?? null) : null,
  )
  const [openMessage, setOpenMessage] = useState<string | null>(
    landed.section === 'support' ? (landed.id ?? null) : null,
  )
  /** The person's own account, over whatever they were looking at. */
  const [profileOpen, setProfileOpen] = useState(false)
  const [passwordOpen, setPasswordOpen] = useState(false)
  /** 9.1's no-show row opens the bookings table searched for that person. */
  const [bookingSearch, setBookingSearch] = useState<string | undefined>(undefined)
  /** One professional's appointments, opened from the figure that counts them. */
  const [bookingsOf, setBookingsOf] = useState<{ id: string; name: string } | null>(null)
  /** And one member's, opened the same way from their row or their record. */
  const [bookingsFor, setBookingsFor] = useState<{ id: string; name: string } | null>(null)
  /** 9.1's provider rows open the table, filtered — never a record. */
  const [providerFilter, setProviderFilter] = useState<'invited' | undefined>(undefined)
  /** A class being edited, or `new` for one being made. */
  const [openClass, setOpenClass] = useState<{ id: string; status: string } | null>(
    landed.section === 'classes' && landed.id
      ? { id: landed.id, status: landed.status ?? 'draft' }
      : null,
  )
  const [openEvent, setOpenEvent] = useState<string | null>(
    landed.section === 'events' ? (landed.id ?? null) : null,
  )
  /** The activity log narrowed to one person, by account id — `/activity/<id>`. */
  const [activityOf, setActivityOf] = useState<string | null>(
    landed.section === 'activity' ? (landed.id ?? null) : null,
  )
  /** Which page was refused and what it needed. 9.9 names both. */
  const [refused, setRefused] = useState<{ page: string; permission: string } | null>(null)

  /**
   * The address bar, kept in step with the desk in both directions.
   *
   * Out: whatever is open becomes `#/members/abc`, so reloading lands there
   * and the address can be sent to somebody. In: pressing the browser's back
   * button changes the hash, and the desk follows it — which is the whole
   * point, because before this it left the desk entirely.
   *
   * The two halves cannot chase each other: each only acts when the address
   * and the desk actually disagree.
   */
  const place: Place = {
    section,
    ...((openMember ?? openProvider ?? openBooking ?? openMessage ?? openEvent ?? openClass?.id ?? activityOf)
      ? {
          id:
            openMember ??
            openProvider ??
            openBooking ??
            openMessage ??
            openEvent ??
            openClass?.id ??
            activityOf ??
            undefined,
        }
      : {}),
    ...(openClass ? { status: openClass.status } : {}),
  }

  /** Sets everything the address can say, and clears everything it cannot. */
  const goTo = useCallback((to: Place) => {
    setOpenProvider(to.section === 'providers' ? (to.id ?? null) : null)
    setOpenMember(to.section === 'members' ? (to.id ?? null) : null)
    setOpenBooking(to.section === 'bookings' ? (to.id ?? null) : null)
    setOpenMessage(to.section === 'support' ? (to.id ?? null) : null)
    setOpenEvent(to.section === 'events' ? (to.id ?? null) : null)
    setActivityOf(to.section === 'activity' ? (to.id ?? null) : null)
    setOpenClass(
      to.section === 'classes' && to.id ? { id: to.id, status: to.status ?? 'draft' } : null,
    )
    /* The intents, which are not in the address and must not survive a move.
       See `whereYouAre.ts` for why they are deliberately left out of it. */
    setSuspendOnOpen(false)
    setPauseOnOpen(false)
    setPaymentRef(undefined)
    setBookingSearch(undefined)
    setBookingsOf(null)
    setBookingsFor(null)
    setProviderFilter(undefined)
    setRefused(null)
    setSection(to.section)
  }, [])

  const address = writePlace(place)
  useEffect(() => {
    if (window.location.pathname === address) return
    /* `replace` when the address did not name a place — landing on `/` and
       becoming `/overview` is not somewhere the person went, and a back
       button that has to be pressed twice to leave the first screen is its
       own annoyance. A move they made is pushed, so Back returns from it. */
    const named = readPlace(window.location.pathname).section !== 'overview'
    if (named) window.history.pushState(null, '', address)
    else window.history.replaceState(null, '', address)
  }, [address])

  useEffect(() => {
    /* `popstate` rather than `hashchange`: the address is a real path now, so
       the browser's own Back and Forward are what move through it. */
    const followed = () => {
      const to = readPlace(window.location.pathname)
      if (!samePlace(to, readPlace(address))) goTo(to)
    }
    window.addEventListener('popstate', followed)
    return () => window.removeEventListener('popstate', followed)
  }, [address, goTo])

  /**
   * The number beside a rail item — board 9.1 draws *"Providers 2"*.
   *
   * `AppShell` has taken a `badge` since it was built and **nothing ever
   * passed one**, so the board's count existed as a prop and never as a
   * number on a screen. It is the attention panel counted by where each row
   * opens, which is the board's own rule — *"every alert is a route, not a
   * notice"* — so the badge and the panel can never disagree about how many
   * things there are.
   *
   * The same query 9.1 runs, so this is a cache hit rather than a second
   * request, and it is skipped for a role that cannot read the dashboard.
   */
  const dashboard = useGetDashboardQuery(undefined, {
    skip: !user.permissions.includes('read'),
  })
  const badges = new Map<string, number>()
  for (const item of dashboard.data?.attention ?? []) {
    const key = ATTENTION_DESTINATION[item.kind]?.slice(1)
    if (key) badges.set(key, (badges.get(key) ?? 0) + 1)
  }
  const rail = RAIL.map((s) => {
    const n = badges.get(s.key)
    return n ? { ...s, badge: n } : s
  })

  /**
   * 9.8 — *"a line saying what this role can do: open and read, not act."*
   *
   * Drawn from the permissions rather than from the role's name, so a role the
   * client invents next month gets the right line without anybody editing this.
   */
  const readOnly = !ACTING_PERMISSIONS.some((p) => user.permissions.includes(p))

  /**
   * Opening a record is its own permission, and the desk was not checking it.
   *
   * 9.2a: *"Finance cannot open either — **Finance cannot open a provider or
   * booking record**."* Both routes behind those panels are `read_records`,
   * which the Finance preset does not hold and the Support preset does — and
   * the row opened the panel for anybody, so Finance got a 403 drawn as
   * *"that professional could not be loaded"*: a failure, where the product
   * has a written refusal and a screen (9.9) whose whole job is to deliver
   * it. Checked before the panel is opened, from the row that opens it.
   */
  const openRecord = (page: string, open: () => void) => {
    if (!user.permissions.includes('read_records')) {
      return setRefused({ page, permission: 'read_records' })
    }
    open()
  }

  /**
   * The same refusal, when the way in was the address bar.
   *
   * Every press into a page this role may not open goes through `setRefused`
   * — but a typed, bookmarked or pasted address never presses anything. It
   * lands straight on the section, so Support reaching `/payments` got
   * *"That did not load"* over a 403, and `/settings` drew the whole settings
   * screen with its Add buttons live for a role the rail hides it from. Found
   * by walking the desk as each role on 2026-09-27.
   *
   * Derived rather than stored, so it holds for every way the address can
   * change — landing, Back and Forward — without each of them remembering.
   */
  const fromAddress = refusalFor(place, user.permissions)
  const refusal = refused ?? fromAddress

  /**
   * Whether what is on screen is a **list**, which fills the window and
   * scrolls its own rows, or a page that scrolls as a whole.
   *
   * Per branch rather than per section, because the same section is both: the
   * members table fills the window, and one member's record does not.
   */
  const showingAList =
    refusal === null &&
    ((section === 'members' && openMember === null) ||
      (section === 'providers' && openProvider === null) ||
      (section === 'bookings' && openBooking === null) ||
      section === 'payments' ||
      (section === 'classes' && openClass === null) ||
      (section === 'events' && openEvent === null) ||
      (section === 'support' && openMessage === null) ||
      /* Settings is nine tabs over a table: the strip stays put and the
         section under it scrolls, which is the same shape as a list. */
      section === 'settings' ||
      section === 'activity' ||
      /* The host's console: three figures and four tabs over a list that is
         being written to while somebody watches it. */
      openClass?.status === 'live' ||
      openClass?.status === 'console')

  return (
    <PermissionProvider permissions={user.permissions} roles={user.rolesWith}>
      <AppShell
        sections={rail}
        current={section}
        /* One place decides what a move clears, so the rail and the browser's
           back button can never disagree about it. Pressing Bookings in the
           rail means all of them: leaving one professional's filter set would
           give somebody one diary under a heading that says every appointment
           on the platform. */
        onNavigate={(key) => goTo({ section: key })}
        user={{ fullName: user.fullName, roleName: user.roleName }}
        onOpenProfile={() => setProfileOpen(true)}
        onChangePassword={() => setPasswordOpen(true)}
        onSignOut={onSignOut}
      >
        {/* The line 9.8 asks for, above every table rather than on one screen:
            it is a fact about the role, not about the page. */}
        {readOnly && <StatusBanner tone="info">{READ_ONLY_SAID}</StatusBanner>}

        <ErrorBoundary
          resetKey={`${section}:${openMember ?? ''}:${openProvider ?? ''}:${openBooking ?? ''}:${openMessage ?? ''}:${openClass?.id ?? ''}:${openEvent ?? ''}:${activityOf ?? ''}:${refusal?.page ?? ''}`}
        >
          {/* One spinner for whichever screen is arriving. The chunk is
              fetched the first time a section is opened and cached after
              that, so this is a flash once per screen per visit. */}
          <Suspense fallback={<Spinner label="Opening" />}>
            <PageBody fills={showingAList}>
              {refusal !== null && (
                <NoAccess
                  page={refusal.page}
                  permission={refusal.permission}
                  signedInAs={{ fullName: user.fullName, roleName: user.roleName }}
                  onBack={() => (fromAddress ? goTo({ section: 'overview' }) : setRefused(null))}
                />
              )}
              {refusal === null && section === 'overview' && (
                <Overview
                  /**
                   * 9.9 is reached from here and nowhere else.
                   *
                   * The board is explicit about it: *"the Overview alert about a
                   * refused payment is shown to every role, but only Operations
                   * and Finance can open it. Rather than the alert silently doing
                   * nothing, it explains."* Every attention row is shown to
                   * everybody, because a Support administrator needs to be able
                   * to tell a caller what is happening — so some of them open
                   * something this role may not see, and that is the refusal.
                   */
                  onOpen={(href, id, label, kind) => {
                    if (href === '/providers') {
                      if (!user.permissions.includes('read'))
                        return setRefused({ page: 'Providers', permission: 'read' })
                      /**
                       * The **table**, filtered — not the record.
                       *
                       * `user-flows.md` names all three ways out of this panel
                       * and two of them are tables: *"an invitation not finished
                       * -> 9.2 Providers, filtered to Invited"* and *"a full
                       * category -> 9.2"*. Opening a record was wrong for both,
                       * and for a full category it was worse than wrong: the row
                       * carries a **category** id, which went to a route that
                       * wants a provider and drew "that professional could not
                       * be loaded" about a category.
                       */
                      setProviderFilter(kind === 'category_full' ? undefined : 'invited')
                      setSection('providers')
                    } else if (href === '/members') {
                      /**
                       * Their record, which is where the decision is made.
                       *
                       * 4.8's `review` raises a person and somebody decides; the
                       * deciding is a Suspend button that names their
                       * appointments and asks why, and that lives on the member
                       * record. Before the Members screen existed this row
                       * opened the bookings table searched for their name, which
                       * is the same information with nothing to do about it.
                       */
                      if (!user.permissions.includes('read_records'))
                        return setRefused({ page: 'a member record', permission: 'read_records' })
                      setSection('members')
                      setOpenMember(id)
                    } else {
                      /* `ATTENTION_DESTINATION` is a `Record<AttentionKind, …>`
                       over three destinations, and the other two are above, so
                       this is Payments. There was a fourth branch here sending a
                       row to the bookings table; no kind has pointed at it since
                       the missed-appointment row started opening a member
                       record, which is where the decision is actually made. */
                      if (!user.permissions.includes('money'))
                        return setRefused({ page: 'Payments', permission: 'money' })
                      /* Filtered to that payment. The row names one — "HI-4822,
                       $140.00, the time is still held" — and landing on the
                       whole table makes somebody find it again by hand. 9.5a's
                       "find the payment" already worked this way; this row,
                       which is the more urgent of the two, did not. */
                      setPaymentRef(label)
                      setSection('payments')
                    }
                  }}
                />
              )}
              {refusal === null &&
                section === 'members' &&
                (openMember ? (
                  <MemberRecord
                    accountId={openMember}
                    {...(suspendOnOpen ? { askToSuspend: true } : {})}
                    onBack={() => {
                      setOpenMember(null)
                      setSuspendOnOpen(false)
                    }}
                    onSeeActivity={(id) => goTo({ section: 'activity', id })}
                    /* Their appointments live on the bookings table, searched for
                     them — the same door 9.1's missed-appointment row uses, and
                     for the same reason: a booking is found by name here. */
                    /* Their appointments, by account rather than by name: the
                     search matches the contact written on a booking, which
                     can be somebody else's, and two members can share a
                     name. See `account_id` on the bookings route. */
                    onFindBookings={(id, name) => {
                      setOpenMember(null)
                      setBookingsOf(null)
                      setBookingSearch(undefined)
                      setBookingsFor({ id, name })
                      setSection('bookings')
                    }}
                  />
                ) : (
                  <Members
                    onOpen={(id) => openRecord('a member record', () => setOpenMember(id))}
                    onFindBookings={(id, name) => {
                      setBookingsOf(null)
                      setBookingSearch(undefined)
                      setBookingsFor({ id, name })
                      setSection('bookings')
                    }}
                    /* Straight to the record with the question open: the decision
                     needs their appointments in front of whoever is making it. */
                    onSuspend={(id) =>
                      openRecord('a member record', () => {
                        setSuspendOnOpen(true)
                        setOpenMember(id)
                      })
                    }
                  />
                ))}
              {refusal === null &&
                section === 'providers' &&
                (openProvider ? (
                  <ProviderRecord
                    providerId={openProvider}
                    {...(pauseOnOpen ? { askToPause: true } : {})}
                    onBack={() => {
                      setOpenProvider(null)
                      setPauseOnOpen(false)
                    }}
                    onSeeActivity={(id) => goTo({ section: 'activity', id })}
                    /* Their appointments, by id rather than by name — see
                     `provider_id` on the bookings route, which had been
                     waiting for a caller since it was written. */
                    onFindBookings={(id, name) => {
                      setOpenProvider(null)
                      setPauseOnOpen(false)
                      /* One narrowing at a time: a name search *and* a
                       professional would be two filters nobody asked for. */
                      setBookingSearch(undefined)
                      setBookingsFor(null)
                      setBookingsOf({ id, name })
                      setSection('bookings')
                    }}
                  />
                ) : (
                  <Providers
                    onOpen={(id) => openRecord('a provider record', () => setOpenProvider(id))}
                    /* To the decision, not past it — pausing weighs appointments
                     that are listed on the record. */
                    onPause={(id) =>
                      openRecord('a provider record', () => {
                        setPauseOnOpen(true)
                        setOpenProvider(id)
                      })
                    }
                    {...(providerFilter ? { openOn: providerFilter } : {})}
                  />
                ))}
              {refusal === null &&
                section === 'bookings' &&
                (openBooking ? (
                  <BookingRecord
                    bookingId={openBooking}
                    onBack={() => setOpenBooking(null)}
                    onFindPayment={(reference) => {
                      setOpenBooking(null)
                      setPaymentRef(reference)
                      setSection('payments')
                    }}
                    /* Either name on the appointment opens its record. Both go
                     through `openRecord`, so a role that may not read records
                     gets 9.9's refusal rather than a blank screen. */
                    onOpenMember={(id) => {
                      setOpenBooking(null)
                      openRecord('a member record', () => {
                        setSection('members')
                        setOpenMember(id)
                      })
                    }}
                    onOpenProvider={(id) => {
                      setOpenBooking(null)
                      openRecord('a provider record', () => {
                        setSection('providers')
                        setOpenProvider(id)
                      })
                    }}
                  />
                ) : (
                  <Bookings
                    onOpen={(id) => openRecord('a booking record', () => setOpenBooking(id))}
                    {...(bookingSearch ? { search: bookingSearch } : {})}
                    {...(bookingsOf
                      ? { providerId: bookingsOf.id, providerName: bookingsOf.name }
                      : {})}
                    {...(bookingsFor
                      ? { accountId: bookingsFor.id, memberName: bookingsFor.name }
                      : {})}
                  />
                ))}
              {refusal === null && section === 'payments' && (
                <Payments
                  reference={paymentRef}
                  /* The person who paid, from the row's ⋯. Through `openRecord`
                   so a role that may not read records gets the refusal that
                   names who can. */
                  onOpenMember={(id) =>
                    openRecord('a member record', () => {
                      setPaymentRef(undefined)
                      setSection('members')
                      setOpenMember(id)
                    })
                  }
                />
              )}
              {refusal === null &&
                section === 'classes' &&
                (openClass === null ? (
                  <Classes
                    onOpen={(id, status) => setOpenClass({ id, status })}
                    onCreate={() => setOpenClass({ id: 'new', status: 'draft' })}
                    onRunIt={(id) => setOpenClass({ id, status: 'console' })}
                  />
                ) : /* Three screens, one row. A class that has run is a record; a
                     class running *now* is the host's console; anything else is
                     the form. The row knows which, so the next screen does not
                     have to fetch the class again to find out. */
                openClass.status === 'finished' || openClass.status === 'cancelled' ? (
                  <ClassRecord classId={openClass.id} onBack={() => setOpenClass(null)} />
                ) : openClass.status === 'live' || openClass.status === 'console' ? (
                  <ClassRoom classId={openClass.id} onBack={() => setOpenClass(null)} />
                ) : (
                  /* A sheet, like the event editor — so the classes table stays
                   behind it. `Classes` is rendered above by the same branch. */
                  <>
                    <Classes
                      onOpen={(id, status) => setOpenClass({ id, status })}
                      onCreate={() => setOpenClass({ id: 'new', status: 'draft' })}
                      onRunIt={(id) => setOpenClass({ id, status: 'console' })}
                    />
                    <ClassEditor
                      {...(openClass.id === 'new' ? {} : { classId: openClass.id })}
                      onBack={() => setOpenClass(null)}
                    />
                  </>
                ))}
              {/**
               * The list stays, and the editor comes over it.
               *
               * 9.7a is a sheet now — the prototype draws it as an overlay and
               * the client asked for the same — so it is rendered **beside** the
               * table rather than instead of it. Editing one row of a list is
               * not leaving the list, and coming back used to put you at the top
               * of it having lost your place.
               */}
              {refusal === null && section === 'events' && (
                <>
                  <Events onOpen={setOpenEvent} onCreate={() => setOpenEvent('new')} />
                  {openEvent && (
                    <EventEditor
                      {...(openEvent === 'new' ? {} : { eventId: openEvent })}
                      onBack={() => setOpenEvent(null)}
                    />
                  )}
                </>
              )}
              {/* 9.10 — an old or mistyped link. It has no way in until there is
                a router to mistype a URL into; the screen exists and is tested. */}
              {refusal === null && section === 'not-found' && (
                <NotFound onBack={() => setSection('overview')} />
              )}
              {refusal === null && section === 'settings' && <Settings />}
              {refusal === null && section === 'activity' && (
                <Activity
                  {...(activityOf ? { accountId: activityOf } : {})}
                  onPerson={(id) => goTo({ section: 'activity', ...(id ? { id } : {}) })}
                  onOpenMember={(id) => openRecord('a member record', () => goTo({ section: 'members', id }))}
                  onOpenProvider={(id) => openRecord('a provider record', () => goTo({ section: 'providers', id }))}
                />
              )}
              {refusal === null &&
                section === 'support' &&
                (openMessage ? (
                  <SupportMessageRecord
                    messageId={openMessage}
                    onBack={() => setOpenMessage(null)}
                    /* Whoever wrote in, where they have an account. Through
                     `openRecord` like every other one of these. */
                    onOpenMember={(id) => {
                      setOpenMessage(null)
                      openRecord('a member record', () => {
                        setSection('members')
                        setOpenMember(id)
                      })
                    }}
                  />
                ) : (
                  <SupportInbox onOpen={setOpenMessage} />
                ))}
            </PageBody>
          </Suspense>
        </ErrorBoundary>
        {/* Outside the error boundary's reset key: the person's own account
            is not one of the screens, and it stays open across a navigation
            the way a sheet should. */}
        <ProfileSheet open={profileOpen} onOpenChange={setProfileOpen} />
        <PasswordSheet open={passwordOpen} onOpenChange={setPasswordOpen} />
      </AppShell>
    </PermissionProvider>
  )
}
